import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';

export interface AlertModalPrimaryButton {
  label: string;
  onClick: () => void;
}

interface AlertModalProps {
  open: boolean;
  message: string;
  onClose: () => void;
  title?: string;
  /** When set, shows Cancel + primary actions (same dialog chrome as a single-button alert). */
  primaryButton?: AlertModalPrimaryButton;
  cancelLabel?: string;
}

const AlertModal: React.FC<AlertModalProps> = ({
  open,
  message,
  onClose,
  title = 'Alert',
  primaryButton,
  cancelLabel = 'Cancel',
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: (theme) => ({
          borderRadius: '16px',
          background: theme.palette.white?.main || theme.palette.background.paper,
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
          position: 'relative',
          overflow: 'hidden',
          '&:before': {
            content: '""',
            display: 'block',
            background: `linear-gradient(268.67deg, ${
              theme.palette.themeColorSecondary?.main || '#00839A'
            } -16.55%, ${theme.palette.themeColor?.main || '#00A8C5'} 73.37%)`,
            height: '16px',
            borderRadius: '16px 16px 0px 0px',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
          },
          '&:focus': {
            outline: '0 !important',
          },
          [theme.breakpoints.up('lg')]: {
            minWidth: '521px !important',
          },
          [theme.breakpoints.up('sm')]: {
            minWidth: '465px !important',
            maxWidth: '500px !important',
          },
          '@media screen and (max-width:600px)': {
            minWidth: '95% !important',
          },
        }),
      }}
    >
      <DialogTitle sx={{ padding: '24px 24px 0 24px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '20px',
              color: '#000000',
            }}
          >
            {title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme: Theme) => theme.palette.grey[500],
              padding: '4px',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: '24px' }}>
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'pre-line',
            color: '#000000',
            fontSize: '16px',
            lineHeight: '24px',
          }}
        >
          {message}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          padding: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        {primaryButton ? (
          <>
            <Button
              onClick={onClose}
              variant="outlined"
              color="primary"
              sx={{
                padding: '12px 24px',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '16px',
                minWidth: '110px',
                borderRadius: '4px',
              }}
            >
              {cancelLabel}
            </Button>
            <Button
              onClick={() => primaryButton.onClick()}
              variant="contained"
              color="primary"
              autoFocus
              sx={{
                padding: '12px 24px',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '16px',
                minWidth: '110px',
                borderRadius: '4px',
              }}
            >
              {primaryButton.label}
            </Button>
          </>
        ) : (
          <Button
            onClick={onClose}
            variant="contained"
            color="primary"
            autoFocus
            sx={{
              padding: '12px 24px',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '16px',
              minWidth: '110px',
              borderRadius: '4px',
            }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AlertModal;
